import { Location } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import {
  DealerBranchDto, hasDue,
  hasOpen,
  JobDayEnum,
  JobSummaryDto,
  WebLayoutDto
} from "@shared/models";
import { skip, take } from "rxjs/operators";
import { getFullDayName, LayoutAware, NoZeroPipe } from "../../common";
import { SortOrder, TableColumn } from "../../common/components";
import { JobService, ProductSettingService, WebLayoutService } from "../../common/services";

export interface DueTableColumn extends TableColumn {
  day?: JobDayEnum;
}

export interface Grouping {
  id: string;
  name: string;
  lookup?: string;
}

@Component({
  templateUrl: "./summary.component.html",
  styleUrls: ["./summary.component.scss"]
})
export class SummaryComponent extends LayoutAware implements OnInit {
  groupings: Grouping[];

  level1: string;
  level2: string;
  filter1: string;
  filter2: string;

  showJobs: { [key: string]: string } = {
    all: "JobSummary.AllJobs",
    hold: "JobSummary.OnlyHolds",
    nohold: "JobSummary.ExcludeHolds",
  };
  showJob = "all";

  jobSummary: JobSummaryDto[];
  dueDays: JobSummaryDto[];
  openDays: JobSummaryDto[];
  // openDaySort = "";
  // dueDaySort = "";

  dueColumns: DueTableColumn[] = [];
  openColumns: DueTableColumn[] = [];
  dueSort: string;
  openSort: string;

  private readonly summaryCol: DueTableColumn =
    {
      field: "id",
      columnId: "id_link",
      header: "Job.Resource",
      defaultSort: SortOrder.Ascending,
      // sortOrder: SortOrder.Ascending,
      showInFooter: true,
      lookupLong: "ResourceMaster",
      // lookupFunc: this.branchDisplay
    };


  private readonly drilldownCol: DueTableColumn =
    {
      field: "id",
      header: "Job.Resource",
      defaultSort: SortOrder.Ascending,
      // sortOrder: SortOrder.Ascending,
      showInFooter: true,
      lookupLong: "ResourceMaster",
      // lookupFunc: this.branchDisplay
    };

  private readonly baseDueColumns: DueTableColumn[] = [];
  private readonly baseOpenColumns: DueTableColumn[] = [];

  constructor(
    private route: ActivatedRoute,
    private jobService: JobService,
    private productSetting: ProductSettingService,
    private router: Router,
    private location: Location,
    layoutService: WebLayoutService
  ) {
    super("OpenJobSummary", layoutService);

    this.groupings = [
      ...(productSetting.booleanValue("JobAtBranch") ? [{ id: "dealer", name: "Job.Branch", lookup: "DealerBranch" }] : []),
      { id: "statusId", name: "Job.StatusID", lookup: "CodeJobStatus" },
      // { id: "group", name: "Group" },
      { id: "resourceId", name: "Job.Resource", lookup: "ResourceMaster" },
      { id: "usercaptureid", name: "JobSummary.User" },
      { id: "typeId", name: "Job.TypeID", lookup: "CodeJobType" },
      { id: "owner", name: "JobSummary.Owner" },
      { id: "machine.brand", name: "Machine.Category1", lookup: "CodeMachineCategory" },
      { id: "resource.manager", name: "Resource.Manager" },
      { id: "hold", name: "Job.Holds" },
      { id: "error", name: "Job.Errors" },
      // { id: "controller", name: "Controller" },
    ];

    const includeWeekend = this.productSetting.booleanValue("JobIncludesWeekend");
    this.baseDueColumns = [
      {
        field: "totalJobs",
        header: "JobSummary.Total",
        defaultSort: SortOrder.Descending,
        class: "text-right w-10",
        tdClass: "align-middle text-right pr-3",
        showInFooter: true,
        skipFilter: true
      },
      {
        field: "overdue",
        header: "JobSummary.Overdue",
        day: JobDayEnum.Overdue,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "yesterday",
        header: getFullDayName(-1, includeWeekend),
        day: JobDayEnum.Yesterday,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "bookedDay0",
        header: "JobSummary.Today",
        day: JobDayEnum.Today,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "bookedDay1",
        header: getFullDayName(1, includeWeekend),
        day: JobDayEnum.Tomorrow,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "bookedDay2",
        header: getFullDayName(2, includeWeekend),
        day: JobDayEnum.Tomorrow2,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "later",
        header: "JobSummary.Later",
        day: JobDayEnum.Later,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "text-center align-middle",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      }
    ];
    this.baseOpenColumns = [
      {
        field: "totalJobs",
        header: "JobSummary.Total",
        defaultSort: SortOrder.Descending,
        class: "text-right w-10",
        tdClass: "align-middle text-right pr-3",
        showInFooter: true,
        skipFilter: true
      },
      {
        field: "daysAverage",
        header: "JobSummary.Average",
        day: null,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "align-middle text-center",
        skipFilter: true
      },
      {
        field: "days1",
        header: this.productSetting.label("JobSummaryDays1"),
        day: JobDayEnum.Days1,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "align-middle text-center",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "days2",
        header: this.productSetting.label("JobSummaryDays2"),
        day: JobDayEnum.Days2,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "align-middle text-center",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "days3",
        header: this.productSetting.label("JobSummaryDays3"),
        day: JobDayEnum.Days3,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "align-middle text-center",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        field: "days4",
        header: this.productSetting.label("JobSummaryDays4"),
        day: JobDayEnum.Days4,
        defaultSort: SortOrder.Descending,
        class: "text-center w-10",
        tdClass: "align-middle text-center",
        showInFooter: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      }
    ];
  }

  navigate(mode: string) {
    const rParams: any = {};

    if (this.level1) {
      rParams.level1 = this.level1;
    }
    if (this.level2 && this.filter1) {
      rParams.level2 = this.level2;
    }
    if (this.filter1) {
      rParams.filter1 = this.filter1;
    }
    if (this.filter2) {
      rParams.filter2 = this.filter2;
    }
    if (this.layoutId) {
      rParams.layoutId = this.layoutId;
    }
    rParams.showJob = this.showJob;

    this.router.navigate(["./", mode, rParams], { relativeTo: this.route });
  }

  /**
   * First time load - get layouts
   * Subsequent Params change - check for layout change
   */
  ngOnInit() {
    // Fetch and check layout
    this.route.paramMap
    .pipe(
      take(1),
      this.notDisposed()
    )
    .subscribe(r => {
      this.layoutId = r.get('layoutId') || null;
      this.fetchLayouts();
    });
    // Check Layout Change or just do normal search
    this.route.paramMap
    .pipe(
      skip(1),
      this.notDisposed()
    )
    .subscribe(r => {
      // if layout changed... apply layout and return
      if(this.layoutChanged(this.layoutId, r.get('layoutId'))) {
        this.layoutId = r.get('layoutId') || null;
        this.setLayout(this.layouts.find(l => l.layoutId === this.layoutId));// layout change will trigger the Search via column changes
      } else {
        this.applyParamsAndShowSummary(
          this.getParamsFromParamMap(r)
        );
      }
    });
  }

  getParamsFromParamMap(r: ParamMap): any {
    return {
      level1: r.get('level1'),
      filter1: r.get('filter1'),
      level2: r.get('level2'),
      filter2: r.get('filter2'),
      layoutId: r.get('layoutId'),
      showJob: r.get('showJob')
    };
  }

  applyParamsAndShowSummary(params: any) {
    this.showSummary(
      params?.level1,
      params?.filter1,
      params?.level2,
      params?.filter2,
      params?.layoutId,
      params?.showJob || "all",
    ).subscribe(r => {
      this.jobSummary = r;
      this.dueDays = this.jobSummary.filter(s => hasDue(s));
      this.openDays = this.jobSummary.filter(s => hasOpen(s));
    });
  }

  private showSummary(
    level1: string,
    filter1: string,
    level2: string,
    filter2: string,
    layoutId: string,
    showJob: string,
  ) {
    this.layoutId = layoutId || null;
    this.jobSummary = null;
    this.dueDays = null;
    this.openDays = null;

    this.filter1 = filter1;
    this.filter2 = filter2;
    this.level1 = level1 || "statusId";
    this.level2 = level2 || (this.level1 === "statusId" ? "resourceId" : "statusId");
    let summaryCols = [];
    this.showJob = showJob;

    const level = this.filter1 && this.level2 ? this.level2 : this.level1;
    if (this.filter1 && this.level2) {
      summaryCols = [this.drilldownCol];
    } else {
      summaryCols = [this.summaryCol];
    }
    const group = this.groupings.find(g => g.id === level);
    summaryCols[0].header = group.name;
    summaryCols[0].lookupLong = group.lookup;


    this.dueColumns = [...summaryCols, ...this.baseDueColumns];
    // this.dueSort = this.dueColumns[0].field;

    this.openColumns = [...summaryCols, ...this.baseOpenColumns];
    // this.openSort = this.openColumns[0].field;

    return this.jobService.getSummary(
      this.level1,
      this.filter1,
      this.level2,
      this.filter2,
      this.showJob
    );
  }

  getDueColumns(): string[] {
    return this.baseDueColumns.filter(c => c.day).map(c => c.field);
  }

  getOpenColumns(): string[] {
    return this.baseOpenColumns.filter(c => c.day).map(c => c.field);
  }

  getDueClass(col: JobDayEnum) {
    switch (col) {
      case JobDayEnum.Days3:
        return "btn-block bg-warning text-light font-weight-bold";
      case JobDayEnum.Overdue:
      case JobDayEnum.Yesterday:
      case JobDayEnum.Days4:
        return "btn-block bg-danger text-light font-weight-bold";
      case JobDayEnum.Days2:
      case JobDayEnum.Today:
        return "btn-block bg-blue text-light font-weight-bold";
      case JobDayEnum.Tomorrow:
      case JobDayEnum.Tomorrow2:
      case JobDayEnum.Later:
      case JobDayEnum.Days1:
        return "btn-block bg-success text-light font-weight-bold";
    }
  }

  groupLevel1(id: string) {
    this.filter1 = "";
    if (id) {
      this.level1 = id;
    }
    this.groupLevel2(null, true);
  }

  groupLevel2(id: string, force = false) {
    this.level2 = id;
    if (this.level2 && this.level1 === this.level2) {
      this.level2 = null;
    }
    this.filter2 = "";
    if (!this.level1 || (this.level2 && !this.filter1 && !force)) {
      // this.summaryRows = [];
      return;
    }

    const params: any = {};
    if (this.level1) {
      params.level1 = this.level1;
    }
    if (this.level2) {
      params.level2 = this.level2;
    }
    if (this.filter1) {
      params.filter1 = this.filter1;
    }
    if (this.filter2) {
      params.filter2 = this.filter2;
    }
    if (this.filter2) {
      params.filter2 = this.filter2;
    }
    if (this.showJob) {
      params.showJob = this.showJob;
    }
    if (this.layoutId) {
      params.layoutId = this.layoutId;
    }

    params.showJob = this.showJob;

    this.router.navigate([".", params], { relativeTo: this.route });
  }

  summaryLink(row: JobSummaryDto, col: any): any[] {
    const result: any[] = ["../listlite"];

    const params: any = {};

    const id1 = (this.level2 && this.filter1 ? this.filter1 : row.id) || "~";
    params[this.level1] = id1;

    if (this.filter1) {
      const id2 = row.id || "~";
      params[this.level2] = id2;
    }

    const day =  col ? col.day : JobDayEnum.All;
    params.jobday =  JobDayEnum[day];
    if (this.showJob && this.showJob !== "all")
      params.hold = this.showJob;

    result.push(params);
    return result;
  }

  drilldownLink(row: JobSummaryDto): any[] {
    const params: any = {};
    if (this.layoutId) {
      params.layoutId = this.layoutId;
    }

    if (this.level1) {
      params.level1 = this.level1;
    }
    if (this.level2) {
      params.level2 = this.level2;
    }

    const id = row.id || "~";
    if (this.filter1) {
      params.filter1 = this.filter1;
      if (this.filter2) {
        params.filter2 = id;
      }
    } else {
      params.filter1 = id;
    }
    return [params];
  }

  branchDisplay(item: DealerBranchDto): string {
    return item ? item.dealerId + " ~ " + item.description : "";
  }

  getBranchCode(item: JobSummaryDto): string {
    return item ? item.id.split("_")[1] : "";
  }

  tabPageChange(event: NgbNavChangeEvent) {
    if (event.nextId === "back") {
      this.location.back();
    }
  }

  dealerSort(column: TableColumn): boolean {
    if (column.field !== "dealerId") {
      return false;
    }
  }

  getDealer(row: JobSummaryDto): string {
    return row.id.split("_")[0];
  }

  groupingName(id: string): string {
    const grp = this.groupings.find(g => g.id === id);
    return grp ? grp.name : "";
  }

  sumDue(col: string): number {
    return this.dueDays
      ? this.dueDays.reduce(
          (total: number, r: JobSummaryDto) => total + r[col],
          0
        )
      : 0;
  }

  sumOpen(col: string): number {
    return this.openDays
      ? this.openDays.reduce(
          (total: number, r: JobSummaryDto) => total + r[col],
          0
        )
      : 0;
  }

  layoutExtras(data: any) {
    data.level1 = this.level1;
    data.level2 = this.level2;
    data.filter1 = this.filter1;
    data.filter2 = this.filter2;
    data.showJob = this.showJob;
  }

  // OLD: set params and navigate...
  // NEW: firs navigate to layoutid, then set params
  setLayout(layout: WebLayoutDto) {
    if(layout) {
      const cLayout = { ...layout.data };
      cLayout.layoutId = this.layoutId;

      const params: any = {};
      if (cLayout.level1) {
        params.level1 = cLayout.level1;
      }
      if (cLayout.level2) {
        params.level2 = cLayout.level2;
      }
      if (cLayout.filter1) {
        params.filter1 = cLayout.filter1;
      }
      if (cLayout.filter2) {
        params.filter2 = cLayout.filter2;
      }
      if (cLayout.filter2) {
        params.filter2 = cLayout.filter2;
      }
      if (cLayout.showJob) {
        params.showJob = cLayout.showJob;
      }
      if (cLayout.layoutId) {
        params.layoutId = cLayout.layoutId;
      }

      this.applyParamsAndShowSummary(params);
    } else {
      this.applyParamsAndShowSummary(this.getParamsFromParamMap(this.route.snapshot.paramMap));
    }
  }

  public showJobParams(showJob: string): any {
    const params: any = {showJob};
    if (this.level1) {
      params.level1 = this.level1;
    }
    if (this.level2) {
      params.level2 = this.level2;
    }
    if (this.filter1) {
      params.filter1 = this.filter1;
    }
    if (this.filter2) {
      params.filter2 = this.filter2;
    }
    if (this.filter2) {
      params.filter2 = this.filter2;
    }
    return params;
  }
}
